import { DatePicker, Divider, Select } from 'antd';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import dayjs from 'dayjs';
import React, { memo, useState } from 'react';

const { RangePicker } = DatePicker;

const presetOptions = [
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Previous month' },
];

interface IProps {
  value?: string;
  placeholder?: string;
  size?: SizeType;
  style?: React.CSSProperties;
  popupMatchSelectWidth?: boolean;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  onChange: (value: string) => void;
  /** Optional custom range callback. When provided, a date-range picker is shown at the bottom of the dropdown. */
  onCustomRange?: (from: string | undefined, to: string | undefined) => void;
  /** Current custom range bounds, used to render the "Custom: from – to" label when value === 'custom'. */
  customFrom?: string;
  customTo?: string;
  /** Presets this host has no use for, by value - the side panel drops "Previous month". */
  hidePresets?: string[];
  /** Adds "No date": for a list whose rows may have no date at all. Its value is 'no_date'. */
  allowNoDate?: boolean;
}

const formatCustomLabel = (from?: string, to?: string) => {
  if (!from || !to) return 'Custom';
  return `${dayjs(from).format('DD MMM')} – ${dayjs(to).format('DD MMM YYYY')}`;
};

export const DateRangeSelect: React.FC<IProps> = memo(({
  value,
  placeholder = 'Date range',
  size,
  style,
  popupMatchSelectWidth,
  getPopupContainer,
  onChange,
  onCustomRange,
  customFrom,
  customTo,
  hidePresets,
  allowNoDate,
}) => {
  const [open, setOpen] = useState(false);
  const [rangePickerOpen, setRangePickerOpen] = useState(false);

  const options = [
    ...presetOptions.filter((o) => !hidePresets?.includes(o.value)),
    ...(allowNoDate ? [{ value: 'no_date', label: 'No date' }] : []),
    ...(value === 'custom'
      ? [{ value: 'custom', label: formatCustomLabel(customFrom, customTo) }]
      : []),
  ];

  return (
    <Select
      allowClear
      size={size}
      placeholder={placeholder}
      style={{ width: '100%', ...style }}
      options={options}
      value={value}
      open={open}
      onDropdownVisibleChange={(visible) => {
        // Keep the dropdown open while user is picking start/end in the embedded RangePicker
        // (its popup is portaled to document.body, which the Select treats as click-outside).
        if (!visible && rangePickerOpen) return;
        setOpen(visible);
      }}
      popupMatchSelectWidth={popupMatchSelectWidth}
      getPopupContainer={getPopupContainer}
      onChange={(v) => {
        onChange(v);
        if (v !== 'custom') {
          onCustomRange?.(undefined, undefined);
          setRangePickerOpen(false);
        }
      }}
      dropdownRender={onCustomRange ? (menu) => (
        <>
          {menu}
          <Divider style={{ margin: '4px 0' }} />
          <div
            style={{
              position: 'relative',
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: 14,
              color: 'rgba(0, 0, 0, 0.88)',
            }}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            Custom range…
            <RangePicker
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
              }}
              onOpenChange={setRangePickerOpen}
              onChange={(range: any) => {
                if (range?.[0] && range?.[1]) {
                  const from = range[0].startOf('day').toISOString();
                  const to = range[1].endOf('day').toISOString();
                  onCustomRange(from, to);
                  onChange('custom');
                  setRangePickerOpen(false);
                  setOpen(false);
                }
              }}
            />
          </div>
        </>
      ) : undefined}
    />
  );
});
